import React from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  InboxIcon,
  FolderOpenIcon,
  BellIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface EmptyStateProps {
  icon?: React.ComponentType<any>;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon = InboxIcon,
  title,
  description,
  action,
  secondaryAction,
  className = '',
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`flex flex-col items-center justify-center py-12 px-4 ${className}`}
    >
      {/* Icon with animated background */}
      <div className="relative">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute inset-0 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-xl"
        />
        <div className="relative bg-gray-100 dark:bg-gray-800 rounded-full p-6">
          <Icon className="h-12 w-12 text-gray-400 dark:text-gray-500" />
        </div>
      </div>

      {/* Content */}
      <h3 className="mt-6 text-lg font-semibold text-gray-900 dark:text-white text-center">
        {title}
      </h3>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 text-center max-w-sm">
        {description}
      </p>

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          {action && (
            <button
              onClick={action.onClick}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Pre-configured empty states for common scenarios
export const NoApplicationsEmpty: React.FC<{
  onCreateNew?: () => void;
}> = ({ onCreateNew }) => (
  <EmptyState
    icon={DocumentTextIcon}
    title="No RTI Applications Yet"
    description="You haven't submitted any RTI applications yet. Get started by creating your first application."
    action={
      onCreateNew
        ? {
            label: 'Create New Application',
            onClick: onCreateNew,
          }
        : undefined
    }
  />
);

export const NoSearchResultsEmpty: React.FC<{
  onClearSearch?: () => void;
}> = ({ onClearSearch }) => (
  <EmptyState
    icon={MagnifyingGlassIcon}
    title="No Results Found"
    description="We couldn't find any applications matching your search criteria. Try adjusting your filters or search terms."
    action={
      onClearSearch
        ? {
            label: 'Clear Filters',
            onClick: onClearSearch,
          }
        : undefined
    }
  />
);

export const NoNotificationsEmpty: React.FC = () => (
  <EmptyState
    icon={BellIcon}
    title="No Notifications"
    description="You're all caught up! There are no new notifications at the moment."
  />
);

export const NoDataEmpty: React.FC<{
  title?: string;
  description?: string;
}> = ({ title, description }) => (
  <EmptyState
    icon={ChartBarIcon}
    title={title || 'No Data Available'}
    description={description || 'There is no data to display at the moment. Please check back later.'}
  />
);

export const ErrorEmpty: React.FC<{
  onRetry?: () => void;
}> = ({ onRetry }) => (
  <EmptyState
    icon={ExclamationCircleIcon}
    title="Oops! Something went wrong"
    description="We encountered an error while loading this content. Please try again."
    action={
      onRetry
        ? {
            label: 'Try Again',
            onClick: onRetry,
          }
        : undefined
    }
  />
);

export const NoFilesEmpty: React.FC<{
  onUpload?: () => void;
}> = ({ onUpload }) => (
  <EmptyState
    icon={FolderOpenIcon}
    title="No Files Uploaded"
    description="You haven't uploaded any documents yet. Upload supporting documents for your RTI application."
    action={
      onUpload
        ? {
            label: 'Upload Files',
            onClick: onUpload,
          }
        : undefined
    }
  />
);
